import { Check as CheckIcon } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Clear";
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_DRAG_HANDLE_SELECTOR } from "../../../../app/constants";
import { useAppDispatch } from "../../../../app/hooks";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { isoFormat } from "../../../../common/helpers/formatDateTime";
import { CustomerOrganizationsResult } from "../../../customer/types/queries";
import { UnionListItem } from "../../../settings/Unions/unionsApi";
import { getUnions } from "../../../settings/Unions/unionsSlice";
import { TalentUnionPayment, UnionOption } from "../../../talents/talentAPI";
import {
  doTradeUnionsAutocompleteQuery,
  fetchEezyCompaniesAutocomplete,
} from "../../../talents/talentsSlice";

interface Props {
  onEditCancel: () => void;
  onEditSubmit: (value: TalentUnionPayment) => void;
  item: TalentUnionPayment | undefined;
  modalAction: string;
  talentId: string;
  loading: boolean;
  setLoading: (value: boolean) => void;
}

const AddEditModal = ({
  onEditCancel,
  onEditSubmit,
  item,
  modalAction,
  talentId,
  loading,
  setLoading,
}: Props) => {
  const toDateObject = (dateString: string): Date | null => {
    const [year, month, day] = dateString.split("-").map(Number);
    const dateObject = new Date(year, month - 1, day);
    return isNaN(dateObject.getTime()) ? null : dateObject;
  };

  const dispatch = useAppDispatch();

  const [unionInputValue, setUnionInputValue] = useState("");
  const [companyInputValue, setCompanyInputValue] = useState("");

  const [newItem, setNewItem] = useState<TalentUnionPayment>(
    item
      ? { ...item }
      : {
          id: "",
          talentId: "",
          tradeUnionId: "",
          tradeUnionName: "",
          tradeUnionAcronym: "",
          eezyCompanyName: "",
          eezyCompanyId: "",
          validFrom: "",
          validTo: "",
        },
  );

  const [unionSearchHistory, setUnionSearchHistory] = useState<UnionOption[]>(
    [],
  );

  const [companySearchHistory, setCompanySearchHistory] = useState<
    CustomerOrganizationsResult[]
  >([]);

  const [unionsList, setUnionsList] = useState<UnionListItem[]>([]);

  const { t } = useTranslation();

  useEffect(() => {
    // Fetch all unions once to get list names
    dispatch(getUnions({})).then((res: any) => {
      const unionsData = res.payload as UnionListItem[];
      setUnionsList([...unionsData]);
    });
  }, [dispatch]);

  useEffect(() => {
    // Initialize input value with newItem.tradeUnionName if it exists
    setUnionInputValue(newItem.tradeUnionName || "");
  }, [newItem.tradeUnionName]);

  useEffect(() => {
    // Initialize input value with newItem.eezyCompanyName if it exists
    setCompanyInputValue(newItem.eezyCompanyName || "");
  }, [newItem.eezyCompanyName]);

  const updateDate = (date: Date | null, type: "validFrom" | "validTo") => {
    const isValidDate = date && !isNaN(date.getTime());

    setNewItem((prevItem) => ({
      ...prevItem,
      [type]: isValidDate ? format(date, isoFormat) : "",
    }));
  };

  const handleUnionChange = (value: UnionOption) => {
    setNewItem({
      ...newItem,
      tradeUnionId: value.id,
      tradeUnionName: value.name,
      tradeUnionAcronym: value.acronym,
    });
  };

  const handleUnionValChange = (value: string) => {
    value &&
      dispatch(doTradeUnionsAutocompleteQuery({ name: value })).then(
        (action: any) => {
          if (isFulfilled(action)) {
            const payload = action.payload;

            // Add parentUnion name to each item
            const items = payload.map((item: UnionOption) => {
              const parentUnionName =
                unionsList.find(
                  (union: UnionListItem) => union.id === item.parentId,
                )?.name || item.name;
              return {
                ...item,
                parentUnionName: parentUnionName,
              };
            });

            // Check if newItem's id is not empty and not already in the search history
            const newItemExists =
              newItem.tradeUnionId &&
              !items.some(
                (item: UnionOption) => item.id === newItem.tradeUnionId,
              );

            // Only add newItem to the search history if it passes the checks
            const updatedItems = newItemExists
              ? [
                  ...items,
                  {
                    id: newItem.tradeUnionId,
                    parentId:
                      items.find(
                        (union: UnionOption) =>
                          union.id === newItem.tradeUnionId,
                      ).id || null,
                    name: newItem.tradeUnionName,
                    acronym: newItem.tradeUnionAcronym,
                  },
                ]
              : items;

            setUnionSearchHistory(updatedItems);
          }
        },
      );
  };

  const handleCompanyChange = (value: CustomerOrganizationsResult) => {
    setNewItem({
      ...newItem,
      eezyCompanyId: value.id,
      eezyCompanyName: value.name,
    });
  };

  const handleCompanyValChange = (value: string) => {
    value &&
      dispatch(fetchEezyCompaniesAutocomplete({ name: value })).then(
        (action: any) => {
          if (isFulfilled(action)) {
            const items = action.payload;

            // Check if newItem's id is not empty and not already in the search history
            const newItemExists =
              newItem.eezyCompanyId &&
              !items.some(
                (item: CustomerOrganizationsResult) =>
                  item.id === newItem.eezyCompanyId,
              );

            // Only add newItem to the search history if it passes the checks
            const updatedItems = newItemExists
              ? [
                  ...items,
                  {
                    id: newItem.eezyCompanyId,
                    name: newItem.eezyCompanyName,
                    customerCompanyId: "",
                    customerCompanyBusinessId: "",
                    isRepresentative: false,
                    salesPermission: {
                      verificationDate: "",
                    },
                  },
                ]
              : items;

            setCompanySearchHistory(updatedItems);
          }
        },
      );
  };

  const handleSaveClick = () => {
    // Check if already processing a save operation
    if (loading) return;
    setLoading(true);
    onEditSubmit({
      id: newItem.id,
      talentId: talentId,
      tradeUnionId: newItem.tradeUnionId,
      tradeUnionName: newItem.tradeUnionName,
      tradeUnionAcronym: newItem.tradeUnionAcronym,
      eezyCompanyName: newItem.eezyCompanyName,
      eezyCompanyId: newItem.eezyCompanyId,
      validFrom: newItem.validFrom?.toString().split("T")[0] || "",
      validTo: newItem.validTo?.toString().split("T")[0] || "",
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        background: "#FFFFFF",
        borderRadius: "8px",
        marginTop: "16px",
        padding: "0px 16px 0px 0px",
      }}
    >
      <OverlaySpinner isLoading={loading} />
      <Box
        className={DEFAULT_DRAG_HANDLE_SELECTOR}
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "grab",
        }}
      >
        <DialogTitle>
          <Typography
            variant="header"
            sx={{ fontWeight: 600, fontSize: "14px" }}
          >
            {t("talentProfile:unionMembershipFee").toUpperCase()}
          </Typography>
        </DialogTitle>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "0px 0px 16px 16px",
          fontSize: "12px",
        }}
      >
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  {t("talentProfile:unionName")}
                </TableCell>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  {t("talentProfile:employer")}
                </TableCell>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  {t("talentProfile:startDate")}
                </TableCell>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  {t("talentProfile:endDate")}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  <Autocomplete
                    disabled={modalAction === "edit"}
                    sx={{ width: "200px" }}
                    size="small"
                    disablePortal={false}
                    fullWidth
                    inputValue={unionInputValue}
                    ListboxProps={{
                      style: { maxHeight: "300px", overflow: "visible" },
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    noOptionsText={t("talentProfile:noResults")}
                    // show last searched companies, disable grouping
                    {...(!!unionSearchHistory.length && {
                      groupBy: (option: UnionOption) =>
                        option?.parentUnionName || "",
                    })}
                    getOptionLabel={(option) => option.name}
                    filterOptions={createFilterOptions({
                      stringify: (option) => JSON.stringify(option),
                    })}
                    options={unionSearchHistory}
                    onChange={(_, newValue: UnionOption | null) => {
                      if (newValue) {
                        handleUnionChange(newValue);
                      } else {
                        handleUnionChange({
                          id: "",
                          parentId: "",
                          name: "",
                          acronym: "",
                          tradeUnionNumber: "",
                        });
                        setUnionInputValue("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      // Update state based on user input, avoid action on selection to keep inputValue controlled
                      if (reason === "input") {
                        setUnionInputValue(newInputValue);
                        handleUnionValChange(newInputValue);
                      }
                    }}
                    renderOption={(props, option) => (
                      <ListItem {...props} key={option.id}>
                        <ListItemText
                          primary={
                            <Tooltip title={option.name} placement="top">
                              <Typography
                                sx={{
                                  textTransform: !unionSearchHistory.length
                                    ? "uppercase"
                                    : "unset",
                                  /*
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '200px'
                                  */
                                }}
                              >
                                {`(${option.tradeUnionNumber}) ${option.name}`}
                              </Typography>
                            </Tooltip>
                          }
                        />
                      </ListItem>
                    )}
                    renderGroup={({ group, children }) => (
                      <Fragment key={`unionGroup-${group}`}>
                        <ListSubheader
                          disableSticky
                          sx={{
                            color: "#9167CA",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            lineHeight: "20px",
                          }}
                        >
                          <Typography
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "200px",
                            }}
                            variant="header"
                          >
                            {group}
                          </Typography>
                        </ListSubheader>
                        <List>{children}</List>
                      </Fragment>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          t("talentProfile:findTalentUnion") || "Hae liitto"
                        }
                        name="talentUnion"
                        required
                      />
                    )}
                  />
                </TableCell>

                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  <Autocomplete
                    disabled={modalAction === "edit"}
                    sx={{ width: "200px" }}
                    size="small"
                    fullWidth
                    inputValue={companyInputValue}
                    disablePortal={false}
                    ListboxProps={{
                      style: { maxHeight: "300px", overflow: "visible" },
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    noOptionsText={t("talentProfile:noResults")}
                    getOptionLabel={(option) => option.name}
                    filterOptions={createFilterOptions({
                      stringify: (option) => JSON.stringify(option),
                    })}
                    options={companySearchHistory}
                    onChange={(
                      _,
                      newValue: CustomerOrganizationsResult | null,
                    ) => {
                      if (newValue) {
                        handleCompanyChange(newValue);
                      } else {
                        handleCompanyChange({
                          id: "",
                          name: "",
                          businessId: "",
                          customerCompanyId: "",
                          customerCompanyBusinessId: "",
                          isRepresentative: false,
                          salesPermission: { verificationDate: "" },
                        });
                        setCompanyInputValue("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      // Update state based on user input, avoid action on selection to keep inputValue controlled
                      if (reason === "input") {
                        setCompanyInputValue(newInputValue);
                        handleCompanyValChange(newInputValue);
                      }
                    }}
                    renderOption={(props, option) => (
                      <ListItem {...props}>
                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                textTransform: !companySearchHistory.length
                                  ? "uppercase"
                                  : "unset",
                              }}
                            >
                              {option.name}
                            </Typography>
                          }
                        />
                      </ListItem>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          t("talentProfile:findEezyCompany") || "Hae liitto"
                        }
                        name="eezyCompany"
                        required
                      />
                    )}
                  />
                </TableCell>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  <DatePicker
                    format="dd.MM.yyyy"
                    value={
                      newItem.validFrom ? toDateObject(newItem.validFrom) : null
                    }
                    onChange={(date) => updateDate(date, "validFrom")}
                    slotProps={{
                      textField: {
                        size: "small",
                        label: null,
                        sx: {
                          width: "200px",
                          paddingTop: "0px",
                          border: "none",
                        },
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  <DatePicker
                    format="dd.MM.yyyy"
                    value={
                      newItem.validTo ? toDateObject(newItem.validTo) : null
                    }
                    onChange={(date) => updateDate(date, "validTo")}
                    slotProps={{
                      textField: {
                        size: "small",
                        label: null,
                        sx: {
                          width: "200px",
                          paddingTop: "0px",
                          border: "none",
                        },
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          className={DEFAULT_DRAG_HANDLE_SELECTOR}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginTop="16px"
          sx={{ cursor: "grab" }}
        >
          <Button
            sx={{ borderRadius: "15px", maxHeight: "30px" }}
            startIcon={<CancelIcon />}
            variant="outlined"
            onClick={onEditCancel}
          >
            {t("common:cancel")}
          </Button>
          <Button
            sx={{ borderRadius: "15px", maxHeight: "30px" }}
            startIcon={<CheckIcon />}
            variant="contained"
            onClick={() => {
              handleSaveClick();
            }}
            disabled={
              loading ||
              !newItem.tradeUnionId ||
              !newItem.eezyCompanyId ||
              !newItem.validFrom ||
              (!!newItem.validFrom &&
                !!newItem.validTo &&
                newItem.validFrom > newItem.validTo)
            }
          >
            {t("common:complete")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddEditModal;
